import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFileText, FiDollarSign, FiShield, FiRefreshCw, FiAlertTriangle, FiEdit } = FiIcons;

const TermsConditions = () => {
  const terms = [
    {
      icon: FiFileText,
      title: 'Purpose of Services',
      content: 'All services provided by Tutor Hub Online are meant for guidance, learning, and reference purposes only. Students must use the provided materials responsibly and in compliance with their institution\'s academic policies.'
    },
    {
      icon: FiDollarSign,
      title: 'Payments',
      content: [
        'All payments must be completed in advance before services are delivered unless otherwise agreed.',
        'Fees once paid are non-refundable except in cases where Tutor Hub Online fails to deliver the agreed service.'
      ]
    },
    {
      icon: FiShield,
      title: 'Confidentiality & Privacy',
      content: [
        'Tutor Hub Online ensures complete confidentiality of all student information.',
        'Personal data will not be shared with third parties without consent.'
      ]
    },
    {
      icon: FiRefreshCw,
      title: 'Revisions',
      content: 'We provide revisions for assignments if requested within a reasonable period, based on the original instructions provided.'
    },
    {
      icon: FiAlertTriangle,
      title: 'Limitation of Liability',
      content: [
        'Any academic consequences (such as grades, penalties, or disciplinary actions) arising from the misuse of our services.',
        'Delays caused by incomplete or unclear instructions provided by the student.'
      ]
    },
    {
      icon: FiEdit,
      title: 'Modifications to Terms',
      content: 'Tutor Hub Online reserves the right to update or modify these Terms & Conditions at any time without prior notice. Students are encouraged to review this page regularly.'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Tutor Hub Online – Terms & Conditions
          </h1>
          <h2 className="text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto">
            Please read these terms carefully before using our services. By accessing or using Tutor Hub Online, you agree to these conditions.
          </h2>
        </div>
      </section>

      {/* General Terms */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">General Terms</h2>
          </div>
          <div className="space-y-8">
            {terms.map((term, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm border">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <SafeIcon icon={term.icon} className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {index + 1}. {term.title}
                    </h3>
                    {Array.isArray(term.content) ? (
                      <ul className="space-y-2">
                        {term.content.map((item, itemIndex) => (
                          <li key={itemIndex} className="text-gray-600 flex items-start">
                            <span className="text-primary mr-2 mt-2">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600">{term.content}</p>
                    )}
                    {term.title === 'Limitation of Liability' && (
                      <p className="text-gray-700 font-medium mt-3">
                        Tutor Hub Online shall not be held responsible for:
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8">
            <div className="flex items-start space-x-4">
              <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-semibold text-yellow-800 mb-3">Important Notice</h3>
                <p className="text-yellow-700">
                  By continuing to use Tutor Hub Online, you acknowledge that you have read, understood, and agreed to these Terms & Conditions. These terms are designed to protect both our students and our service quality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Questions about our Terms & Conditions?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            If you have any questions or need clarification about these terms, our support team is here to help.
          </p>
          <Link
            to="/contact"
            className="bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-colors text-lg"
          >
            👉 Contact Us for Clarification
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;